import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CloseIcon from '@mui/icons-material/Close';

import { MapContainer, TileLayer, Marker, Popup, useMap, Tooltip as RLTooltip } from 'react-leaflet';
import type { Map as LeafletMap } from 'leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { storeService } from '../services/storeService';

// Fix Leaflet default marker icons in bundlers
// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png',
});

type LatLng = { latitude: number; longitude: number };

type DriverLocationPayload = {
  location: LatLng;
  lastUpdate: Date | null;
};

function parseDriverLocationPayload(data: any): DriverLocationPayload | null {
  const loc = data?.currentLocation || data?.location;
  if (!loc || typeof loc.latitude !== 'number' || typeof loc.longitude !== 'number') {
    return null;
  }

  const ts = data?.lastUpdate || data?.lastLocationUpdate;
  return {
    location: { latitude: loc.latitude, longitude: loc.longitude },
    lastUpdate: ts && typeof ts.toDate === 'function' ? ts.toDate() : null,
  };
}

interface DriverLiveTrackingModalProps {
  open: boolean;
  onClose: () => void;
  driverId: string;
  driverName?: string;
  driverPhotoUrl?: string;
  initialLocation?: LatLng | null;
}

const DEFAULT_CENTER: [number, number] = [33.5731, -7.5898]; // Casablanca

function getInitials(name?: string): string {
  if (!name) return 'L';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts[1]?.[0] || '';
  return (first + last).toUpperCase() || (first.toUpperCase() || 'L');
}

function createAvatarIcon(name: string, photoUrl?: string) {
  const content = photoUrl
    ? `<img src="${photoUrl}" alt="${name}" style="width:40px;height:40px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 6px rgba(0,0,0,.35);object-fit:cover;background:#eee;" />`
    : `<div style="width:40px;height:40px;border-radius:50%;background:#1abc9c;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;border:2px solid #fff;box-shadow:0 0 6px rgba(0,0,0,.35);">${getInitials(name)}</div>`;

  return L.divIcon({
    className: 'driver-avatar-marker',
    html: `<div style="display:flex;align-items:center;justify-content:center">${content}</div>`,
    iconSize: [46, 56],
    iconAnchor: [23, 46],
    popupAnchor: [0, -48],
  });
}

function createStoreIcon(label: string) {
  const html = `
    <div style="display:flex;align-items:center;justify-content:center">
      <div style="width:34px;height:34px;border-radius:8px;background:#e74c3c;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;border:2px solid #fff;box-shadow:0 0 6px rgba(0,0,0,.35);">
        🏪
      </div>
    </div>`;
  return L.divIcon({
    className: 'store-marker',
    html,
    iconSize: [40, 48],
    iconAnchor: [20, 44],
    popupAnchor: [0, -42],
  });
}

function RecenterMap({ position }: { position: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    map.setView(position, 15, { animate: true });
  }, [map, position]);
  return null;
}

function SetMapRef({ onReady }: { onReady: (map: LeafletMap) => void }) {
  const map = useMap();
  useEffect(() => {
    onReady(map);
  }, [map, onReady]);
  return null;
}

function FitToBounds({ positions }: { positions: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length === 0) return;
    if (positions.length === 1) {
      map.setView(positions[0], 14, { animate: true });
      return;
    }
    const bounds = L.latLngBounds(positions.map((p) => L.latLng(p[0], p[1])));
    map.fitBounds(bounds.pad(0.15), { animate: true });
  }, [map, positions]);
  return null;
}

const DriverLiveTrackingModal: React.FC<DriverLiveTrackingModalProps> = ({
  open,
  onClose,
  driverId,
  driverName,
  driverPhotoUrl,
  initialLocation
}) => {
  const [position, setPosition] = useState<[number, number] | null>(
    initialLocation ? [initialLocation.latitude, initialLocation.longitude] : null
  );
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [hasLocation, setHasLocation] = useState<boolean>(!!initialLocation);
  const [loading, setLoading] = useState<boolean>(true);

  const mapRef = useRef<LeafletMap | null>(null);
  const hasDedicatedLocationRef = useRef(false);
  const [store, setStore] = useState<{ lat: number; lng: number; name: string } | null>(null);

  useEffect(() => {
    if (!open || !driverId) return;

    const applyLocation = (payload: DriverLocationPayload | null) => {
      if (!payload) {
        setHasLocation(false);
        return;
      }

      const nextPos: [number, number] = [
        payload.location.latitude,
        payload.location.longitude,
      ];
      setPosition((prev) => {
        // Smooth pan only if moved significantly
        if (
          mapRef.current &&
          (!prev ||
            Math.abs(prev[0] - nextPos[0]) > 0.0005 ||
            Math.abs(prev[1] - nextPos[1]) > 0.0005)
        ) {
          mapRef.current.panTo(nextPos, { animate: true });
        }
        return nextPos;
      });
      setHasLocation(true);
      if (payload.lastUpdate) {
        setLastUpdate(payload.lastUpdate);
      }
    };

    const locationRef = doc(db, 'driver_locations', driverId);
    const legacyRef = doc(db, 'drivers', driverId);

    const unsubLocation = onSnapshot(
      locationRef,
      (snap) => {
        setLoading(false);
        if (snap.exists()) {
          const payload = parseDriverLocationPayload(snap.data());
          hasDedicatedLocationRef.current = payload !== null;
          if (payload) {
            applyLocation(payload);
          }
        } else {
          hasDedicatedLocationRef.current = false;
        }
      },
      (err) => {
        console.error('Error subscribing to driver location:', err);
        setLoading(false);
      }
    );

    const unsubLegacy = onSnapshot(
      legacyRef,
      (snap) => {
        if (hasDedicatedLocationRef.current) return;
        setLoading(false);
        applyLocation(snap.exists() ? parseDriverLocationPayload(snap.data()) : null);
      },
      (err) => {
        console.error('Error subscribing to legacy driver location:', err);
        setLoading(false);
      }
    );

    return () => {
      unsubLocation();
      unsubLegacy();
    };
  }, [open, driverId]);

  // Fetch delivery-responsible store location
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const s = await storeService.getDeliveryResponsibleStore();
        if (s?.location) {
          setStore({ lat: s.location.latitude, lng: s.location.longitude, name: s.name || 'Magasin' });
        } else {
          setStore(null);
        }
      } catch {
        setStore(null);
      }
    })();
  }, [open]);

  const mapCenter = useMemo<[number, number]>(() => {
    if (position) return position;
    if (initialLocation) return [initialLocation.latitude, initialLocation.longitude];
    return DEFAULT_CENTER;
  }, [position, initialLocation]);

  const allPositions = useMemo<[number, number][]>(() => {
    const arr: [number, number][] = [];
    if (position) arr.push(position);
    if (store) arr.push([store.lat, store.lng]);
    return arr;
  }, [position, store]);

  const openInGoogleMaps = () => {
    if (!position) return;
    const [lat, lng] = position;
    const url = `https://www.google.com/maps?q=${lat},${lng}&zoom=15`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const formattedUpdate = lastUpdate
    ? `${lastUpdate.toLocaleDateString()} ${lastUpdate.toLocaleTimeString()}`
    : '—';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '80vh' } }}
    >
      <DialogTitle sx={{ pr: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">
              Suivi temps réel — {driverName || 'Livreur'}
            </Typography>
            <Chip
              size="small"
              color={hasLocation ? 'success' : 'default'}
              label={hasLocation ? 'Localisation active' : 'Localisation indisponible'}
            />
          </Stack>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Ouvrir dans Google Maps">
              <span>
                <IconButton onClick={openInGoogleMaps} color="primary" disabled={!position}>
                  <OpenInNewIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Fermer">
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ width: '100%', height: '60vh' }}>
          <MapContainer
            center={mapCenter}
            zoom={position ? 15 : 12}
            style={{ width: '100%', height: '100%' }}
          >
            <SetMapRef onReady={(m) => (mapRef.current = m)} />
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitToBounds positions={allPositions} />
            {store && (
              <Marker position={[store.lat, store.lng]} icon={createStoreIcon(store.name)}>
                <RLTooltip permanent direction="top" offset={[0, -46]}>
                  <span style={{ fontWeight: 600 }}>{`Magasin: ${store.name}`}</span>
                </RLTooltip>
                <Popup>
                  <Typography variant="subtitle2" component="div">{store.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {store.lat.toFixed(5)}, {store.lng.toFixed(5)}
                  </Typography>
                </Popup>
              </Marker>
            )}
            {position && (
              <>
                <Marker position={position} icon={createAvatarIcon(driverName || 'Livreur', driverPhotoUrl)}>
                  <RLTooltip permanent direction="top" offset={[0, -58]}>
                    <span style={{ fontWeight: 600 }}>{driverName || 'Livreur'}</span>
                  </RLTooltip>
                  <Popup>
                    <Typography variant="subtitle2" component="div">
                      {driverName || 'Livreur'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {position[0].toFixed(5)}, {position[1].toFixed(5)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Dernière mise à jour: {formattedUpdate}
                    </Typography>
                  </Popup>
                </Marker>
                <RecenterMap position={position} />
              </>
            )}
          </MapContainer>
        </Box>

        <Box sx={{ p: 2 }}>
          {loading ? (
            <Typography variant="body2" color="text.secondary">
              Chargement de la localisation en temps réel…
            </Typography>
          ) : hasLocation ? (
            <Typography variant="body2" color="text.secondary">
              Dernière mise à jour: {formattedUpdate}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Aucune localisation disponible pour ce livreur pour le moment.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<MyLocationIcon />}
          onClick={() => {
            if (mapRef.current && position) {
              mapRef.current.setView(position, 16, { animate: true });
            }
          }}
          disabled={!position}
        >
          Recentrer sur le livreur
        </Button>
        <Box flex={1} />
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverLiveTrackingModal;
